import { useState } from 'react'
import { Plus } from 'lucide-react'
import type { Competitor } from '@ispot/shared'
import { CompetitorTile } from './CompetitorTile'

interface CategoryBandProps {
  category: string
  competitors: Competitor[]
  isFirst: boolean
  onOpenCompetitor: (id: string) => void
  onAddToCategory: (category: string) => void
}

type DominantType = 'direct' | 'adjacent' | 'potential'

function dominantType(competitors: Competitor[]): DominantType {
  let direct = 0
  let adjacent = 0
  let potential = 0
  for (const c of competitors) {
    if (c.type === 'direct') direct++
    else if (c.type === 'adjacent') adjacent++
    else potential++
  }
  if (direct > 0) return 'direct'
  if (adjacent > 0) return 'adjacent'
  return 'potential'
}

const ACCENT_COLOR: Record<DominantType, string> = {
  direct: 'var(--color-accent)',
  adjacent: 'var(--color-market)',
  potential: 'var(--color-border-strong)',
}

export function CategoryBand({
  category,
  competitors,
  isFirst,
  onOpenCompetitor,
  onAddToCategory,
}: CategoryBandProps) {
  const [addHover, setAddHover] = useState(false)
  const accentColor = ACCENT_COLOR[dominantType(competitors)]

  const direct = competitors.filter(c => c.type === 'direct').length
  const adjacent = competitors.filter(c => c.type === 'adjacent').length
  const potential = competitors.filter(c => c.type === 'potential').length
  const summaryParts: string[] = []
  if (direct > 0) summaryParts.push(`${direct} direct`)
  if (adjacent > 0) summaryParts.push(`${adjacent} adjacent`)
  if (potential > 0) summaryParts.push(`${potential} potential`)

  return (
    <article
      style={{
        display: 'grid',
        gridTemplateColumns: '176px 1fr',
        columnGap: '32px',
        padding: isFirst ? '12px 0 14px' : '14px 0',
        borderTop: isFirst ? 'none' : '1px solid var(--color-border)',
        flexShrink: 0,
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
        <span
          aria-hidden="true"
          style={{
            width: '24px',
            height: '2px',
            background: accentColor,
            borderRadius: '1px',
            marginBottom: '4px',
          }}
        />
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: '15px',
            letterSpacing: '-0.012em',
            lineHeight: 1.25,
            margin: 0,
            color: 'var(--color-text)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={category}
        >
          {category}
        </h3>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            color: 'var(--color-text-muted)',
            letterSpacing: '0.02em',
            marginTop: '2px',
          }}
        >
          <strong style={{ color: 'var(--color-text)', fontWeight: 500 }}>{competitors.length}</strong>
          {summaryParts.length > 0 && <span style={{ opacity: 0.9 }}> · {summaryParts.join(', ')}</span>}
        </div>
      </header>

      <div
        role="group"
        aria-label={`Competitors in ${category}`}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          alignContent: 'flex-start',
          alignItems: 'flex-start',
          minWidth: 0,
        }}
      >
        {competitors.map(c => (
          <CompetitorTile key={c.id} competitor={c} onOpen={onOpenCompetitor} />
        ))}

        <button
          type="button"
          onClick={() => onAddToCategory(category)}
          onMouseEnter={() => setAddHover(true)}
          onMouseLeave={() => setAddHover(false)}
          aria-label={`Add competitor to ${category}`}
          style={{
            appearance: 'none',
            font: 'inherit',
            background: 'transparent',
            border: `1px dashed ${addHover ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
            color: addHover ? 'var(--color-accent)' : 'var(--color-text-muted)',
            cursor: 'pointer',
            padding: '10px 14px',
            borderRadius: '8px',
            minWidth: '140px',
            maxWidth: '180px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12.5px',
            transition: 'border-color 180ms var(--ease-standard, ease), color 180ms var(--ease-standard, ease)',
          }}
        >
          <Plus size={13} />
          Add competitor
        </button>
      </div>
    </article>
  )
}
